import {
  Image,
  Text,
  Box,
  useToken,
  Button,
  HStack,
  VStack,
  Divider,
  Grid,
  Flex,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { interpretBoldTags } from "../../../utils/processPaywallText";
import { RadioOffIcon } from "../../icons/RadioOffIcon";
import { RadioOnIcon } from "../../icons/RadioOnIcon";
import { SubscriptionOfferV2Type } from "../types";

const MotionBox = motion(Box);
export function SubscriptionSelector({
  isActive,
  onClick,
  borderColor,
  activeBorderColor,
  creatorLogos,
  creatorsCount,
  subOffer,
}: {
  isActive: boolean;
  onClick: () => void;
  borderColor: string;
  activeBorderColor: string;
  creatorLogos?: string[];
  creatorsCount?: number;
  subOffer: SubscriptionOfferV2Type;
}) {
  const borderThemeColor = useToken("colors", activeBorderColor);
  const [resolvedWhite] = useToken("colors", ["white"]);
  const { tier_name, sub_text, price_string } = subOffer;
  const priceStringText = interpretBoldTags(
    price_string,
    700,
    "inherit",
    resolvedWhite,
  );
  const billedText =
    subOffer.billing_frequency === "yearly"
      ? "Billed yearly. Cancel anytime."
      : "Billed monthly. Cancel anytime.";
  return (
    <VStack width="100%" alignItems="end" gap={0}>
      {subOffer.discount_string ? (
        <Box
          borderTopRadius="8px"
          p="4px 16px"
          marginBottom="-1px"
          background="linear-gradient(90deg, #FF5A2C 0%, #EF44BF 100%)"
          boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.10)"
        >
          <Text variant="paymentDiscountString">
            {subOffer.discount_string}
          </Text>
        </Box>
      ) : null}
      <Button
        gc-testing-id={
          subOffer.billing_frequency === "monthly" ? "btnMonthly" : "btnYearly"
        }
        flexDirection="column"
        w="full"
        height="auto"
        onClick={() => onClick()}
        isActive={isActive}
        border="1px solid"
        borderColor={borderColor}
        _active={{
          borderColor: activeBorderColor,
          boxShadow: `0px 0px 20px 0px ${borderThemeColor}`,
        }}
        p={4}
        background="transparent.black.30"
        borderRadius="16px"
        borderTopRightRadius={subOffer.discount_string ? "0px" : "16px"}
        gap={3}
      >
        <HStack align="center" justifyContent="space-between" width="100%">
          <HStack gap={3} alignItems="center">
            {isActive ? (
              <RadioOnIcon
                boxSize={6}
                color="blaze.blaze"
                stroke="blaze.blaze"
                strokeWidth="1px"
              />
            ) : (
              <RadioOffIcon
                boxSize={6}
                color="dune.600"
                stroke="dune.600"
                strokeWidth="1px"
              />
            )}
            <VStack alignItems="flex-start">
              <Text variant="paymentPriceTier">{tier_name}</Text>
              {sub_text ? (
                <HStack gap={1}>
                  {sub_text.icon_url ? (
                    <Image
                      src={sub_text.icon_url}
                      alt={sub_text.text}
                      w="10.5px"
                      h={3}
                    />
                  ) : null}
                  <Text variant="paymentSubText" color={sub_text.color}>
                    {sub_text.text}
                  </Text>
                </HStack>
              ) : null}
            </VStack>
          </HStack>
          <VStack alignItems="flex-end" gap={0}>
            <Text
              variant="paymentPriceString"
              dangerouslySetInnerHTML={{ __html: priceStringText }}
              whiteSpace="normal"
              textAlign="right"
              sx={{
                "& span": {
                  fontSize: { base: "16px", sm: "20px" },
                },
              }}
            />
            {subOffer.original_price_string &&
            (subOffer.has_promo_discount || subOffer.is_intro_pricing) ? (
              <Text
                color="blaze.600"
                fontSize="13px"
                fontWeight="700"
                letterSpacing=".5px"
                lineHeight="140%"
                textDecoration="line-through"
              >
                {subOffer.original_price_string}
              </Text>
            ) : null}
          </VStack>
        </HStack>
        {creatorLogos ? (
          <MotionBox
            initial={false}
            animate={{
              height: isActive ? "auto" : 0,
              opacity: isActive ? 1 : 0,
              marginTop: isActive ? 0 : -12,
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            overflow="hidden"
            width="100%"
          >
            <VStack gap={3} minWidth={0} width="100%">
              <Divider colorScheme="transparent.white.10" />
              <Text variant="paymentSelector" fontWeight="500">
                Supported by All Major Publishers
              </Text>
              <Grid
                templateColumns="repeat(5, 1fr)"
                w="100%"
                gap={{ base: 1, md: 2 }}
                justifyContent="center"
                minWidth={0}
              >
                {creatorLogos.map((logo, i) => (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    key={i}
                    background="black"
                    borderRadius="7px"
                    aspectRatio={1}
                    p={{ base: 1.5, md: 3 }}
                  >
                    <Image src={logo} alt={logo} boxSize="100%" />
                  </Flex>
                ))}
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  background="black"
                  borderRadius="7px"
                  aspectRatio={1}
                  p={{ base: 1.5, md: 3 }}
                >
                  <Text variant="paymentSelector" lineHeight="110%">
                    +{creatorsCount}
                    <br />
                    more
                  </Text>
                </Flex>
              </Grid>
              <Divider colorScheme="transparent.white.10" />
              <Text variant="paymentSelector" textAlign="left">
                {billedText}
              </Text>
            </VStack>
          </MotionBox>
        ) : null}
      </Button>
    </VStack>
  );
}
